import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ChevronRight, Building2 } from "lucide-react";

const roleDestination = (role) => role === "amden_super_admin" ? "/admin" : role === "field_technician" ? "/technician" : "/app/dashboard";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { slug } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState([]);
  const [brand, setBrand] = useState(null);
  const lookupTimer = useRef();

  useEffect(() => { api.get("/demo-users").then((r) => setDemo(r.data)).catch(() => {}); }, []);

  // Branded login via /login/:slug
  useEffect(() => {
    if (!slug) { setBrand(null); return; }
    api.get(`/public/branding/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
  }, [slug]);

  // Live branding detection from the email domain on the generic login page
  useEffect(() => {
    if (slug) return;
    clearTimeout(lookupTimer.current);
    if (!/@[\w.-]+\.\w{2,}$/.test(email.trim())) { setBrand(null); return; }
    lookupTimer.current = setTimeout(() => {
      api.get("/public/branding-by-email", { params: { email: email.trim() } })
        .then((r) => setBrand(r.data)).catch(() => setBrand(null));
    }, 350);
    return () => clearTimeout(lookupTimer.current);
  }, [email, slug]);

  const doLogin = async (e, creds) => {
    e?.preventDefault();
    const em = creds?.email || email, pw = creds?.password || password;
    setLoading(true);
    try {
      const u = await login(em, pw);
      toast.success(`Welcome, ${u.name}`);
      nav(roleDestination(u.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const accent = brand?.accent_color || "#FFA502";
  const accentText = (() => {
    const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(accent);
    if (!m) return "#fff";
    const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16));
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#0F1725" : "#fff";
  })();
  const logoUrl = brand?.has_logo ? `${API}/public/companies/${brand.id}/logo?v=${brand.logo_version || 0}` : null;
  const visibleDemo = brand ? demo.filter((u) => u.company_name === brand.name) : demo;
  const groups = { northgate: "Northgate · Demo Tenant" };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="lg:w-[42%] bg-[#0F1725] text-white p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-16 min-h-[44px]">
            {brand ? (
              logoUrl ? (
                <img data-testid="brand-logo" src={logoUrl} alt={brand.name}
                  className="h-11 max-w-[220px] object-contain bg-white rounded-md px-2.5 py-1.5" />
              ) : (
                <>
                  <div className="h-9 w-9 rounded-md grid place-items-center font-head font-extrabold text-white" style={{ backgroundColor: accent }}>{(brand.name || "?")[0]}</div>
                  <span data-testid="brand-name" className="font-head font-bold text-xl tracking-tight">{brand.name}</span>
                </>
              )
            ) : (
              <>
                <img src="/brand/dispatch-logo.png" alt="Amden Dispatch" className="h-10 w-10 rounded-md" />
                <span className="font-head font-bold text-xl tracking-tight">Amden Dispatch</span>
              </>
            )}
          </div>
          <h1 data-testid="brand-headline" className="font-head font-extrabold text-4xl lg:text-5xl leading-tight">
            {brand ? (brand.trading_name || brand.name) : "Managed Operations Dashboard"}
          </h1>
          <p className="text-slate-300 mt-5 max-w-md text-base">
            {brand
              ? "Sign in to your operations workspace — jobs, scheduling, billing and reports, all in one place."
              : "One secure platform to run field-service operations end-to-end — jobs, scheduling, mobile technicians, billing readiness and Sage-ready exports."}
          </p>
          <div className="mt-10 space-y-3 text-sm text-slate-300">
            {["Multi-tenant, isolated client workspaces", "Configurable modules & roles per company", "Operational status separate from billing status"].map((t) => (
              <div key={t} className="flex items-center gap-2"><ChevronRight className="h-4 w-4 text-slate-500" />{t}</div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-slate-500 text-xs">{brand ? "Powered by Amden Dispatch · Secure by design" : "Amden Dispatch · Secure by design"}</p>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full" style={{ background: `radial-gradient(circle, ${accent}40, transparent 70%)` }} />
      </div>

      {/* Right login */}
      <div className="flex-1 grid place-items-center p-6 lg:p-10 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6 text-slate-500 text-sm"><ShieldCheck className="h-4 w-4" /> Sign in to your workspace</div>
          <form onSubmit={doLogin} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h2 data-testid="signin-heading" className="font-head font-bold text-xl mb-4">{brand ? `Sign in to ${brand.name}` : "Sign in"}</h2>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-600">Email</Label>
                <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.ie" className="mt-1" /></div>
              <div><Label className="text-xs text-slate-600">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" /></div>
            </div>
            <Button data-testid="login-submit" type="submit" className="w-full mt-4 font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: accent, color: accentText }} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Sign in
            </Button>
          </form>

          {visibleDemo.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Demo roles — one-click sign in</p>
              <div className="space-y-3 max-h-72 overflow-y-auto scroll-thin pr-1">
                {["northgate"].filter((g) => visibleDemo.some((u) => u.company === g)).map((g) => (
                  <div key={g}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{groups[g]}</p>
                    <div className="grid grid-cols-1 gap-1.5">
                      {visibleDemo.filter((u) => u.company === g).map((u) => (
                        <button key={u.email} data-testid={`demo-login-${u.email}`}
                          onClick={(e) => doLogin(e, u)} disabled={loading}
                          className="text-left bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-slate-400 transition-colors flex items-center justify-between group">
                          <span><span className="text-sm font-medium text-slate-800">{u.name}</span>
                            <span className="text-xs text-slate-500 block">{u.role_label}</span></span>
                          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
